import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { UploadFile } from "antd";
import { ContactStatus, IContact } from "../../interfaces";

import useAuth from "../../../../hooks/useAuth";
import { EmitError } from "../../../../utils/EmitError";
import { urlHttpGov } from "../../../../services/urls";
import toast from "../../../../utils/toast";
import HelpAndSupportService from "../../../../services/http/services/HelpAndSupportService";
import { EmitSuccessAlert } from "../../../../utils/EmitSuccessAlert";

export interface IContactFormInputs {
    message: string;
    files: UploadFile[];
    status: ContactStatus;
}

const ACCEPTED_FILE_TYPES = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "image/webp",
    "application/pdf",
];

const SUGGESTIONS_RESPONSES = [
    {
        title: "CPF de Terceiro na Nota",
        message:
            "As notas fiscais são computadas exclusivamente no cadastro do titular do CPF na nota. Se emitida no CPF de outra pessoa (esposo, esposa), os bilhetes ficam vinculados àquela conta.",
    },
    {
        title: "Prazo de Importação de Notas",
        message:
            "As notas fiscais levam de 2 a 5 dias úteis para transmissão da SEFAZ e processamento no sistema. Assim que o lote for importado, seus bilhetes e pontos aparecerão automaticamente no app.",
    },
    {
        title: "Regras e Bilhetes de Sorteios",
        message:
            "Os bilhetes são gerados automaticamente com base nas notas válidas do período do sorteio, conforme os divisores municipais. Acompanhe seus bilhetes e números na aba 'Sorteios' do app.",
    },
    {
        title: "Troca de Número / Acesso",
        message:
            "Para atualizar seu telefone, use a opção 'Recuperar Acesso' na tela de login do app. Envie foto do documento e uma selfie para que nossa equipe valide a alteração em até 24h úteis.",
    },
    {
        title: "Nota Fora do Município",
        message:
            "Para participar do programa, as compras precisam ser feitas em estabelecimentos sediados dentro do nosso município. Notas fiscais de outros municípios não geram bilhetes nem pontos locais.",
    },
    {
        title: "Limite por Estabelecimento",
        message:
            "Para garantir concorrência justa entre todos, o regulamento define um limite mensal de bilhetes gerados por estabelecimento. As notas adicionais respeitam esse teto estipulado no app.",
    },
    {
        title: "Erro ao Ler QR Code",
        message:
            "Se a câmera não ler o QR Code devido a falha de impressão, você pode cadastrar a compra digitando a chave de acesso de 44 dígitos através da opção 'Digitar Chave' no próprio aplicativo.",
    },
    {
        title: "Em Análise Técnica",
        message:
            "Recebemos seu relato e nossa equipe técnica já está verificando os dados no sistema para normalização. Assim que tivermos um retorno, avisaremos por aqui. Agradecemos a compreensão!",
    },
    {
        title: "Solicitação Concluída",
        message:
            "Sua solicitação foi analisada e atendida com sucesso! Se precisar de novas orientações, permanecemos à total disposição. Tenha um excelente dia!",
    },
];

export default function useContactForm(
    onSubmitMessage: (message: FormData) => Promise<void>,
    contact: IContact | undefined
) {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [formIsDisabled, setFormIsDisabled] = useState<boolean>(false);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [messageContactIdToDelete, setMessageContactIdToDelete] = useState<number | undefined>();
    const [attachedFiles, setAttachedFiles] = useState<UploadFile[]>([]);

    const {
        handleSubmit,
        formState: { errors },
        control,
        setError,
        setValue,
        watch,
        clearErrors,
    } = useForm<IContactFormInputs>({
        defaultValues: {
            message: "",
            files: [],
            status: contact?.status || ContactStatus.OPEN,
        },
    });

    const watchedMessage = watch("message");
    const { activeCity } = useAuth();

    const handleAddFiles = useCallback(
        (files: FileList | File[]) => {
            const fileArray = Array.from(files);
            const validFiles: UploadFile[] = [];

            for (const file of fileArray) {
                const isValidType =
                    ACCEPTED_FILE_TYPES.includes(file.type.toLowerCase()) ||
                    file.name.toLowerCase().endsWith(".pdf") ||
                    file.name.toLowerCase().endsWith(".png") ||
                    file.name.toLowerCase().endsWith(".jpg") ||
                    file.name.toLowerCase().endsWith(".jpeg") ||
                    file.name.toLowerCase().endsWith(".webp");

                if (!isValidType) {
                    toast({
                        type: "error",
                        text: `Arquivo "${file.name}" inválido! Formatos aceitos: PNG, JPG, JPEG, WEBP e PDF.`,
                        duration: 6000,
                    });
                    continue;
                }

                if (file.size > 10 * 1024 * 1024) {
                    toast({
                        type: "error",
                        text: `Arquivo "${file.name}" muito grande! Tamanho máximo: 10MB.`,
                        duration: 6000,
                    });
                    continue;
                }

                const uploadFile: UploadFile = {
                    uid: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    originFileObj: file as any,
                };
                validFiles.push(uploadFile);
            }

            if (validFiles.length > 0) {
                setAttachedFiles((prev) => {
                    const combined = [...prev, ...validFiles].slice(0, 5);
                    setValue("files", combined);
                    return combined;
                });
            }
        },
        [setValue]
    );

    const handleRemoveFile = useCallback(
        (uid: string) => {
            setAttachedFiles((prev) => {
                const updated = prev.filter((f) => f.uid !== uid);
                setValue("files", updated);
                return updated;
            });
        },
        [setValue]
    );

    const handleSubmitForm: SubmitHandler<IContactFormInputs> = useCallback(
        async (data) => {
            if (!contact || !activeCity?.id) return;

            const trimmedMessage = (data.message || "").trim();
            const hasFiles = attachedFiles.length > 0;

            if (!trimmedMessage && !hasFiles) {
                setError("message", {
                    type: "required",
                    message: "Digite uma mensagem ou anexe um arquivo para enviar.",
                });
                toast({
                    type: "error",
                    text: "Digite uma mensagem ou anexe um arquivo!",
                    duration: 5000,
                });
                return;
            }

            if (trimmedMessage.length > 255) {
                setError("message", {
                    type: "maxLength",
                    message: "A mensagem deve ter no máximo 255 caracteres.",
                });
                toast({
                    type: "error",
                    text: "A mensagem deve ter no máximo 255 caracteres!",
                    duration: 5000,
                });
                return;
            }

            setIsSubmiting(true);
            try {
                const formDataMessage = new FormData();
                formDataMessage.append("message", trimmedMessage || "Arquivo anexado");
                formDataMessage.append("contact_id", contact.id.toString());
                formDataMessage.append("county_id", activeCity.id.toString());

                attachedFiles.forEach((file) => {
                    if (file.originFileObj) {
                        formDataMessage.append("files", file.originFileObj);
                    }
                });

                await onSubmitMessage(formDataMessage);

                setValue("message", "");
                setValue("files", []);
                setAttachedFiles([]);
                clearErrors();
            } catch (err) {
                EmitError(err, true, "contact_send_message_error");
            } finally {
                setIsSubmiting(false);
            }
        },
        [contact, activeCity, attachedFiles, onSubmitMessage, setError, setValue, clearErrors]
    );

    const handleOpenFile = useCallback((fileName: string) => {
        window.open(`${urlHttpGov}/contact/${fileName}`, "_blank");
    }, []);

    const handleClickSuggestion = useCallback(
        (suggestionText: string) => {
            clearErrors("message");
            setValue("message", suggestionText, { shouldValidate: true });
        },
        [clearErrors, setValue]
    );

    const handleDeleteMessage = useCallback((id: number) => {
        setIsDeleteModalVisible(true);
        setMessageContactIdToDelete(id);
    }, []);

    const handleConfirmDeleteMessage = useCallback(async () => {
        if (!messageContactIdToDelete) return;
        try {
            setIsLoadingDelete(true);
            await HelpAndSupportService.deleteMessageContact(messageContactIdToDelete);
            EmitSuccessAlert("Mensagem removida com sucesso!");
            setIsDeleteModalVisible(false);
            setMessageContactIdToDelete(undefined);
        } catch (err) {
            EmitError(err, true, "contact_message_delete_error");
        } finally {
            setIsLoadingDelete(false);
        }
    }, [messageContactIdToDelete]);

    const handleCancelDeleteMessage = useCallback(() => {
        setIsDeleteModalVisible(false);
        setMessageContactIdToDelete(undefined);
    }, []);

    useEffect(() => {
        if (!contact) return;
        setFormIsDisabled(contact.status !== ContactStatus.OPEN);
    }, [contact]);

    return {
        isSubmiting,
        handleSubmit,
        handleSubmitForm,
        control,
        watchedMessage,
        attachedFiles,
        handleAddFiles,
        handleRemoveFile,
        SUGGESTIONS_RESPONSES,
        handleClickSuggestion,
        handleOpenFile,
        errors,
        isLoadingDelete,
        handleCancelDeleteMessage,
        handleConfirmDeleteMessage,
        isDeleteModalVisible,
        handleDeleteMessage,
        messageContactIdToDelete,
        formIsDisabled,
    };
}